import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Collapse, Typography } from 'antd';

import { Container } from '@/ui';
import { QuestionnaireQuestion } from '../../constructor';
import { ProductQuestion } from '@/features/constructorProducts';
import { sortWithNulls } from '@/utils/helpers';
import { useParams } from 'react-router';
import { ConstructorApi as ConstructorQuestionnaireApi } from '../../constructor/api';
import { ConstructorApi as ConstructorProductsApi } from '../../constructorProducts/api';
import { queryClient } from '@/app/QueryClientProvider';
import { adminLocalizationApi, adminLocalizationKeys } from '@/api';
import { SortableItem, SortableList } from '@/components';
import { StyledPanel } from '../styled';
import { usePopups } from '@/hooks';

export const QuestionList = ({ list, type, ...props }) => {
  const [questions, setQuestions] = useState([]);
  const { id } = useParams();
  const { showSuccessToast, showErrorToast } = usePopups();
  const { t } = useTranslation();

  async function updateQuestionOrder(questionId, order) {
    const initialQuestions = [...questions];

    try {
      if (type === 'questionnaire') {
        await ConstructorQuestionnaireApi.updateQuestionOrder({
          id: questionId,
          sortOrder: order.toString()
        });
        await queryClient.refetchQueries({
          queryKey: [adminLocalizationKeys.getQuestionnaireSections],
          queryFn: () =>
            adminLocalizationApi.getQuestionnaireSections({ questionSectionTypeId: id })
        });
      }
      if (type === 'products') {
        await ConstructorProductsApi.updateQuestionOrder({
          id: questionId,
          sortOrder: order.toString()
        });
        await queryClient.refetchQueries({
          queryKey: [adminLocalizationKeys.getProductsSections],
          queryFn: () => adminLocalizationApi.getProductsSections({ productId: id })
        });
      }
      showSuccessToast('Question order is changed');
    } catch (e) {
      setQuestions(initialQuestions);
      showErrorToast(e, true);
    }
  }

  useEffect(() => {
    if (list) {
      setQuestions(
        list?.sort((a, b) => {
          return sortWithNulls(Number(a.sortOrder), Number(b.sortOrder));
        })
      );
    }
  }, [list]);

  return (
    <>
      <Container>
        <Typography.Title level={4}>{t('Questions')}</Typography.Title>
      </Container>

      {questions && type === 'questionnaire' && (
        <div style={{ margin: '16px 0' }}>
          <SortableList list={questions} setList={setQuestions} updateOrder={updateQuestionOrder}>
            {questions.map((question, index) => (
              <SortableItem
                key={question.id}
                id={question.id}
                handle={true}
                value={question.id}
                isLast={questions.length - 1 === index}
                isFirst={index === 0}
              >
                <Collapse bordered={false}>
                  <Collapse.Panel header={question.question} key={question.id}>
                    <QuestionnaireQuestion questionProp={question} {...props} />
                  </Collapse.Panel>
                </Collapse>
              </SortableItem>
            ))}
          </SortableList>
        </div>
      )}

      {questions && type === 'products' && (
        <div style={{ margin: '16px 0' }}>
          <Collapse accordion>
            {questions.map((question) => (
              <StyledPanel header={question.question || question.id} key={question.id}>
                <ProductQuestion questionProp={question} />
              </StyledPanel>
            ))}
          </Collapse>
        </div>
      )}
    </>
  );
};
